/**
 * 音色（波形を作る）
 *
 * 【なぜ純粋な関数で波形まで作るのか】
 * Web Audio のノードを繋いで鳴らす作り方もあるが、それだと
 * **鳴っているものを検査できない。** 音は聴かないと分からない、で終わってしまう。
 *
 * ここは秒数と周波数から `Float32Array` を作るだけの純粋な関数にしてある。
 * そうすると「歪んでいないか（振幅が1を超えていないか）」
 * 「狙った高さで鳴っているか」「終わりに向けて消えているか」を
 * **数で確かめられる**。`AudioContext` は、出来上がった配列を
 * `AudioBuffer` に載せて再生するだけの薄い殻になる（`engine.ts`）。
 *
 * 【乱数を持ち込まない】
 * 息や撥の当たりに雑音を混ぜるが、`Math.random()` は使わない
 * （この作業場では禁じてある）。種を渡して決定論にしてあるので、
 * 同じ指定からは必ず同じ波形が出る。検査が書けるのはそのためである。
 */

import { FastRng } from '@tenka/core';

export interface Voice {
  /**
   * 倍音の振幅。`[1]` なら正弦波、`[1, 0.5, 0.25]` なら
   * 基音と2倍・3倍を混ぜる。**和楽器は倍音の出方で性格が決まる**ので、
   * 波形の種類を選ばせるより、ここを直に書くほうが素直だった。
   */
  readonly harmonics: readonly number[];
  /** 立ち上がり（秒）。撥物は短く、吹き物は長い */
  readonly attack: number;
  readonly decay: number;
  /** 減衰したあと保つ高さ（0〜1）。撥物は 0 に近い */
  readonly sustain: number;
  readonly release: number;
  /** 混ぜる雑音の量（0〜1）。息・撥・打面の当たり */
  readonly noise: number;
  /** 雑音が消えるまでの秒数。当たりだけ欲しいので短い */
  readonly noiseDecay: number;
  /** 鳴り始めの音程のずれ（半音）。太鼓は上から落ちてくる */
  readonly bend: number;
  readonly bendTime: number;
  /** 揺らぎの深さ（半音）と速さ（Hz）。尺八の「ゆり」 */
  readonly vibrato: number;
  readonly vibratoHz: number;
}

const base: Voice = {
  harmonics: [1],
  attack: 0.01,
  decay: 0.1,
  sustain: 0.7,
  release: 0.2,
  noise: 0,
  noiseDecay: 0.05,
  bend: 0,
  bendTime: 0.05,
  vibrato: 0,
  vibratoHz: 5,
};

/**
 * 楽器。**史実の音を再現したものではない**（`scale.ts` と同じ線引き）。
 * 和楽器の鳴り方に寄せた合成音であって、録音ではない。
 */
export const VOICES = {
  /** 尺八。息が多く、ゆっくり立ち上がり、ゆれる */
  shakuhachi: {
    ...base,
    harmonics: [1, 0.12, 0.35, 0.06, 0.12],
    attack: 0.09,
    decay: 0.25,
    sustain: 0.75,
    release: 0.5,
    noise: 0.16,
    noiseDecay: 0.5,
    vibrato: 0.18,
    vibratoHz: 4.6,
  },
  /** 箏。撥いた瞬間が鋭く、あとは長く減る */
  koto: {
    ...base,
    harmonics: [1, 0.6, 0.36, 0.24, 0.14, 0.08],
    attack: 0.004,
    decay: 1.6,
    sustain: 0.0,
    release: 0.5,
    noise: 0.1,
    noiseDecay: 0.03,
  },
  /** 三味線。箏より硬く、短い */
  shamisen: {
    ...base,
    harmonics: [1, 0.75, 0.5, 0.4, 0.28, 0.18, 0.1],
    attack: 0.003,
    decay: 0.7,
    sustain: 0.0,
    release: 0.25,
    noise: 0.22,
    noiseDecay: 0.02,
  },
  /** 太鼓。上から落ちる音程と、打面の当たり */
  taiko: {
    ...base,
    harmonics: [1, 0.3, 0.1],
    attack: 0.002,
    decay: 0.42,
    sustain: 0.0,
    release: 0.2,
    noise: 0.5,
    noiseDecay: 0.06,
    bend: 7,
    bendTime: 0.05,
  },
  /** 鈴。高くて長い */
  suzu: {
    ...base,
    harmonics: [1, 0.9, 0.7, 0.55, 0.4, 0.3, 0.2],
    attack: 0.002,
    decay: 1.1,
    sustain: 0.0,
    release: 0.8,
    noise: 0.3,
    noiseDecay: 0.04,
  },
  /** 法螺貝。低く重く、ゆっくり立ち上がる。合戦の合図 */
  horagai: {
    ...base,
    harmonics: [1, 0.8, 0.55, 0.3, 0.2, 0.12, 0.08],
    attack: 0.16,
    decay: 0.5,
    sustain: 0.8,
    release: 0.7,
    noise: 0.1,
    noiseDecay: 0.7,
    vibrato: 0.1,
    vibratoHz: 3.4,
  },
  /** 拍子木。ごく短い当たりだけ */
  hyoushigi: {
    ...base,
    harmonics: [1, 0.5, 0.8, 0.4],
    attack: 0.001,
    decay: 0.12,
    sustain: 0.0,
    release: 0.06,
    noise: 0.6,
    noiseDecay: 0.02,
  },
} as const satisfies Record<string, Voice>;

export type VoiceName = keyof typeof VOICES;

/** 山を作る。`t` は 0〜1 ではなく秒で受ける */
function envelope(t: number, dur: number, v: Voice): number {
  if (t < 0) return 0;
  if (t < v.attack) return v.attack === 0 ? 1 : t / v.attack;
  const afterAttack = t - v.attack;
  if (t < dur) {
    // 減衰して sustain へ。decay が 0 なら即座に sustain
    const k = v.decay === 0 ? 1 : Math.min(1, afterAttack / v.decay);
    return 1 + (v.sustain - 1) * k;
  }
  // 離した後。ここが 0 に届かないと、音が切れずに次と重なって濁る
  const held = 1 + (v.sustain - 1) * (v.decay === 0 ? 1 : Math.min(1, (dur - v.attack) / v.decay));
  const r = (t - dur) / Math.max(1e-6, v.release);
  return r >= 1 ? 0 : held * (1 - r);
}

/**
 * 1音ぶんの波形を作る。
 *
 * 返る長さは `dur + release` 秒ぶん。**離した後の余韻まで含める** —
 * ここで切ると、鳴り終わりに段差ができて「プツッ」と鳴る。
 */
export function renderNote(
  voice: Voice,
  freq: number,
  dur: number,
  sampleRate: number,
  rng: FastRng,
  gain = 1,
): Float32Array {
  const total = Math.max(1, Math.ceil((dur + voice.release) * sampleRate));
  const out = new Float32Array(total);

  /*
   * 倍音の合計で割って、倍音を増やしても音量が上がらないようにする。
   *
   * **雑音のぶんも数に入れる。** 倍音だけを1に揃えてから雑音を足すと、
   * 合計が 1 を超えて歪む。太鼓（雑音 0.5）で実際に 1.049 まで振れていた。
   * 耳では「なんとなく濁っている」としか分からず、原因に辿り着けない類である。
   */
  const norm = voice.harmonics.reduce((s, h) => s + Math.abs(h), 0) || 1;
  const headroom = 1 / (1 + voice.noise);

  // 位相を持って進める。周波数が動く（bend・vibrato）ので、
  // 「時刻×周波数」で計算すると音程が飛ぶ
  let phase = 0;
  let noiseState = 0;

  for (let i = 0; i < total; i++) {
    const t = i / sampleRate;

    // 音程：落ちてくるぶん（bend）と、ゆれ（vibrato）
    const bendSemis =
      voice.bend === 0 ? 0 : voice.bend * Math.exp(-t / Math.max(1e-6, voice.bendTime));
    const vib = voice.vibrato === 0 ? 0 : voice.vibrato * Math.sin(2 * Math.PI * voice.vibratoHz * t);
    const f = freq * 2 ** ((bendSemis + vib) / 12);

    phase += (2 * Math.PI * f) / sampleRate;

    let s = 0;
    for (let h = 0; h < voice.harmonics.length; h++) {
      const amp = voice.harmonics[h]!;
      if (amp !== 0) s += amp * Math.sin(phase * (h + 1));
    }
    s /= norm;

    if (voice.noise > 0) {
      // 白色をそのまま混ぜると耳に痛いので、少し鈍らせる（一次の平滑）
      const white = (rng.u32() / 0x100000000) * 2 - 1;
      noiseState += (white - noiseState) * 0.45;
      s += noiseState * voice.noise * Math.exp(-t / Math.max(1e-6, voice.noiseDecay));
    }

    out[i] = s * envelope(t, dur, voice) * gain * headroom;
  }
  return out;
}

/**
 * 重ねて1本にする。
 *
 * **足すだけだと 1 を超えて歪む。** 超えた分を潰す（ハードクリップ）と
 * 耳に付く歪みが出るので、なだらかに寝かせる。
 * `tanh` は 0 の近くではほぼ直線で、大きいところだけ寝るので、
 * 小さい音は色を変えずに、大きい音だけ抑えられる。
 */
export function mix(
  parts: readonly { readonly at: number; readonly samples: Float32Array }[],
  seconds: number,
  sampleRate: number,
): Float32Array {
  const out = new Float32Array(Math.max(1, Math.ceil(seconds * sampleRate)));
  for (const p of parts) {
    const start = Math.round(p.at * sampleRate);
    for (let i = 0; i < p.samples.length; i++) {
      const j = start + i;
      if (j < 0) continue;
      // はみ出したぶんは捨てる。輪にするのは呼ぶ側の仕事（`loop`）
      if (j >= out.length) break;
      out[j]! += p.samples[i]!;
    }
  }
  for (let i = 0; i < out.length; i++) out[i] = Math.tanh(out[i]!);
  return out;
}

/**
 * 輪にする（繰り返し再生用）。
 *
 * 末尾からはみ出した余韻を先頭へ回り込ませる。**これをしないと、
 * 繰り返しのたびに余韻が切れて「継ぎ目」が聞こえる。**
 */
export function loop(
  parts: readonly { readonly at: number; readonly samples: Float32Array }[],
  seconds: number,
  sampleRate: number,
): Float32Array {
  const len = Math.max(1, Math.ceil(seconds * sampleRate));
  const out = new Float32Array(len);
  for (const p of parts) {
    const start = Math.round(p.at * sampleRate);
    for (let i = 0; i < p.samples.length; i++) {
      out[(((start + i) % len) + len) % len]! += p.samples[i]!;
    }
  }
  for (let i = 0; i < len; i++) out[i] = Math.tanh(out[i]!);
  return out;
}
